import { useState } from 'react'

import type { FormUpdate, UserFormData } from '../hooks/useUserManagementForm'
import type { UserManagementConfig } from '../UserManagement.config'
import { USER_MANAGEMENT_DEFAULTS as D } from '../UserManagement.constants'

export type UserTypeTabProps = {
    onNext: () => void
    onCancel: () => void
    formData: UserFormData
    update: FormUpdate
    isLoggedInAsUserManager?: boolean
    isSelfEdit?: boolean
    config?: UserManagementConfig
}

const UserTypeTab = ({ onNext, onCancel, formData, update, isLoggedInAsUserManager = false, isSelfEdit = false, config }: UserTypeTabProps) => {
    const [touched, setTouched] = useState(false)

    const handleNext = () => {
        setTouched(true)
        if (!formData.userType) return
        onNext()
    }

    // helpers to look up label/description from config arrays by value, falling back to constants
    const typeLabel = (value: string, fallback: string) =>
        config?.userType?.types?.find(t => t.value === value)?.text ?? fallback
    const typeDesc = (value: string, fallback: string) =>
        config?.userType?.types?.find(t => t.value === value)?.description ?? fallback
    const accessLabel = (value: string, fallback: string) =>
        config?.userType?.accessOptions?.find(a => a.value === value)?.text ?? fallback

    const cancelLabel = config?.cancelLabel ?? D.BTN_CANCEL
    const nextLabel = config?.nextLabel ?? D.BTN_NEXT
    const validationUserType = config?.userType?.validation?.userType ?? D.VALIDATION_USER_TYPE

    return (
        <div>
            <div className="um-heading">{config?.userType?.heading ?? D.HEADING_USER_TYPE}</div>

            {/* Regular User */}
            <div className="um-section">
                <div className="um-radio-row">
                    <input
                        type="radio"
                        name="userType"
                        value={D.USER_TYPE_REGULAR}
                        checked={formData.userType === D.USER_TYPE_REGULAR}
                        disabled={isSelfEdit}
                        onChange={e => update('userType', e.target.value)}
                    />
                    <label><strong>{typeLabel(D.USER_TYPE_REGULAR, D.LABEL_REGULAR_USER)}</strong></label>
                </div>
                <p className="um-description">{typeDesc(D.USER_TYPE_REGULAR, D.DESC_REGULAR_USER)}</p>

                <div className="um-sub-options">
                    <div className="um-checkbox-row">
                        <input
                            type="checkbox"
                            id="sfCaseMgmt"
                            checked={formData.cspAccess}
                            disabled={formData.userType !== D.USER_TYPE_REGULAR}
                            onChange={e => update('cspAccess', e.target.checked)}
                        />
                        <label htmlFor="sfCaseMgmt">{accessLabel('sfCaseMgmt', D.LABEL_SF_CASE_MGMT)}</label>
                    </div>

                    <div className="um-checkbox-row">
                        <input
                            type="checkbox"
                            id="sfPsa"
                            checked={formData.psaAccess}
                            disabled={formData.userType !== D.USER_TYPE_REGULAR}
                            onChange={e => update('psaAccess', e.target.checked)}
                        />
                        <label htmlFor="sfPsa">{accessLabel('sfPsa', D.LABEL_SF_PSA)}</label>
                    </div>
                </div>
            </div>

            {/* COP Administrator */}
            <div className="um-section">
                <div className="um-radio-row">
                    <input
                        type="radio"
                        name="userType"
                        value={D.USER_TYPE_ADMIN}
                        checked={formData.userType === D.USER_TYPE_ADMIN}
                        onChange={e => { update('userType', e.target.value); update('cspAccess', false); update('psaAccess', false) }}
                        disabled={isLoggedInAsUserManager || isSelfEdit}
                    />
                    <label><strong>{typeLabel(D.USER_TYPE_ADMIN, D.LABEL_COP_ADMIN)}</strong></label>
                </div>
                <p className="um-description">{typeDesc(D.USER_TYPE_ADMIN, D.DESC_COP_ADMIN)}</p>
            </div>

            {/* COP User Manager */}
            <div className="um-section">
                <div className="um-radio-row">
                    <input
                        type="radio"
                        name="userType"
                        value={D.USER_TYPE_MANAGER}
                        checked={formData.userType === D.USER_TYPE_MANAGER}
                        onChange={e => { update('userType', e.target.value); update('cspAccess', false); update('psaAccess', false) }}
                        disabled={isLoggedInAsUserManager || isSelfEdit}
                    />
                    <label><strong>{typeLabel(D.USER_TYPE_MANAGER, D.LABEL_COP_USER_MANAGER)}</strong></label>
                </div>
                <p className="um-description">{typeDesc(D.USER_TYPE_MANAGER, D.DESC_COP_USER_MANAGER)}</p>
            </div>

            {touched && !formData.userType && (
                <p className="field-error"><span className="field-error__icon">i</span>{validationUserType}</p>
            )}

            <div className="button-group">
                <button className="btn btn-cancel" onClick={onCancel}>{cancelLabel}</button>
                <button className="btn btn-add" onClick={handleNext}>{nextLabel}</button>
            </div>
        </div>
    )
}

export default UserTypeTab
